import PropTypes from 'prop-types';
import {
  useCallback, useEffect, useState,
} from 'react';
import {useTheme} from 'react-jss';
import {connect} from 'react-redux';
import PencilIcon from '../../../../svg/icons/edit.svg';
import MessageType from '../../../enums/MessageType';
import PlusIcon from '../../../UI/PlusIcon/PlusIcon';
import SelectableContainer from '../../../UI/SelectableContainer/SelectableContainer';
import {setGoogleMapsInitStatus} from '../../actions/delivery-section-actions';
import setCollectionMapZoom from '../../actions/NewCollectionActions/setCollectionMapZoom';
import setSelectedCurrentLocation from '../../actions/NewCollectionActions/setSelectedCurrentLocation';
import {showOverlay} from '../../actions/overlay-actions';
import {
  activateAddCollectionPoint, updatePudoCurrentLocation, showPudoMap,
} from '../../actions/pudo-actions';
import Translation from '../../lang/Translation';
import canEditPudoAddress from '../../selectors/canEditPudoAddress';
import {isMobile} from '../../selectors/DeviceSelectors';
import {getPudo, getSelectedPudoAddress, hasSavedPudoAddresses} from '../../selectors/PudoSelectors';
import {getSettings, getSiteCode} from '../../selectors/selectors';
import googleMapUtils from '../../utils/googleMapUtils';
import V2Components from '../../v2-components';
import NewCollectionPostcodeSearch from '../NewCollection/NewCollectionPostcodeSearch';
import Message from '../ui/Message/Message';
import Spinner from '../ui/Spinner';
import useStyles from './Collection.styles';
import CollectionSavedAddresses from './CollectionSavedAddresses/CollectionSavedAddresses';

export function Collection(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme});
  const [error, setError] = useState(null);
  const [errorFindingStore, setErrorFindingStore] = useState(false);
  const [showGoogleInput, setShowGoogleInput] = useState(false);
  
  const validate = useCallback(
    () => {
      if (props.hasSelectedAddress) {
        setError(null);
        return {isValid: true};
      }
      
      setError('deliveryOptions.requireData.pudo');
      
      return {
        isValid: false,
        errors: [{
          fieldName: 'collection.selectedAddress',
          errorCode: 'deliveryOptions.requireData.pudo',
          pageError: 'errors.mainFormError',
        }],
      };
    }, [props.hasSelectedAddress],
  );
  
  useEffect(() => {
    V2Components.register('Collection', {validate});
    
    if (error && props.hasSelectedAddress) {
      validate();
    }
    
    return () => {
      V2Components.clear('Collection');
    };
  }, [validate, props.hasSelectedAddress]);

  useEffect(() => {
    if (!showGoogleInput && props.shouldUseNewCollectionOverlay) {
      googleMapUtils.loadGoogleMaps()
        .then(() => {
          setShowGoogleInput(true);
        });
    }
  }, []);
  
  const updateSelectedCurrentLocation = (place) => {
    if (place.geometry) {
      const latlng = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      if (props.shouldUseNewCollectionOverlay) {
        props.updatePudoCurrentLocation(latlng, true);
      }
      props.showOverlay();
      props.activateAddCollectionPoint();
  
      try {
        props.setCollectionMapZoom(props.defaultCollectionMapZoomValue);
        props.setSelectedCurrentLocation(latlng);
      } catch (err) {
        setErrorFindingStore(true);
      }
    }
  };
  
  return (
    <div className="Collection">
      {!props.shouldUseNewCollectionOverlay && (
      <CollectionSavedAddresses
        disabled={props.disabled}
      />
      )}
  
      <div className={classes.collectionButtonLabel} data-test="Collection__collectionButtonLabel">
        <Translation optionalExtension={props.siteCode}>collection.addCollectionPointLabel</Translation>
      </div>
      
      {props.shouldUseNewCollectionOverlay && (
        <CollectionSavedAddresses
          disabled={props.disabled}
        />
      )}
  
      {!props.shouldUseNewCollectionOverlay && (
        <SelectableContainer
          componentId="Collection"
          isSelected={false}
          onClick={props.showPudoMap}
          fullWidth
        >
          <div className={classes.collectionContent} data-test="Collection__content">
            {props.isGoogleMapsLoading && (
            <span className={classes.spinnerWrapper}><Spinner size="extra-small" /></span>
            )}
        
            <span className={classes.icon}>
              {props.canEdit ? <img className={classes.pencilIcon} src={PencilIcon} alt="" /> : <PlusIcon />}
            </span>
        
            <Translation optionalExtension={props.siteCode}>
              {props.canEdit ? 'pudo.changeCollectionPoint' : 'pudo.addCollectionPoint'}
            </Translation>
          </div>
        </SelectableContainer>
      )}
      
      {!props.hasSavedAddresses && props.shouldUseNewCollectionOverlay && (
        <div data-test="postcodeSearch">
          {(showGoogleInput) && (
            <NewCollectionPostcodeSearch
              updateSelectedCurrentLocation={updateSelectedCurrentLocation}
              inAddressSection
              isOverlay={false}
            />
          )}
        </div>
      )}
      
      {error && (
        <div className={classes.hasNoSelectedCollectionPointError} data-test="Collection__hasNoSelectedPudoPointError">
          <Message
            type={MessageType.ERROR}
          >
            <Translation optionalExtension={props.siteCode}>{error}</Translation>
          </Message>
        </div>
      )}
    </div>
  );
}

Collection.propTypes = {
  warning: PropTypes.bool,
  disabled: PropTypes.bool,
  
  isGoogleMapsAvailable: PropTypes.bool,
  isGoogleMapsLoading: PropTypes.bool,
  canEdit: PropTypes.bool,
  
  activateAddCollectionPoint: PropTypes.func,
  setGoogleMapsInitStatus: PropTypes.func,
  showPudoMap: PropTypes.func,
  googleMapsSetup: PropTypes.func,
  siteCode: PropTypes.string,
  hasSelectedAddress: PropTypes.bool.isRequired,
  hasSavedAddresses: PropTypes.bool,
  defaultCollectionMapZoomValue: PropTypes.number.isRequired,
  setCollectionMapZoom: PropTypes.func.isRequired,
  setSelectedCurrentLocation: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  country: PropTypes.string,
  shouldUseNewCollectionOverlay: PropTypes.bool.isRequired,
  updatePudoCurrentLocation: PropTypes.func.isRequired,
  showOverlay: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  const {country} = getPudo(state).currentLocation;
  return {
    isGoogleMapsAvailable: state.delivery.isGoogleMapsAvailable,
    isGoogleMapsLoading: state.delivery.isGoogleMapsLoading,
    canEdit: canEditPudoAddress(state),
    siteCode: getSiteCode(state),
    hasSelectedAddress: !!getSelectedPudoAddress(state),
    hasSavedAddresses: !!hasSavedPudoAddresses(state),
    defaultCollectionMapZoomValue: getSettings(state)?.default_map_zoom_value || 13,
    isMobile: isMobile(state),
    country,
    shouldUseNewCollectionOverlay: !!getSettings(state)?.should_use_new_collection_overlay,
  };
}

const mapDispatchToProps = dispatch => ({
  showOverlay,
  setGoogleMapsInitStatus,
  setCollectionMapZoom,
  setSelectedCurrentLocation,
  activateAddCollectionPoint: (name, value) => {
    dispatch(activateAddCollectionPoint(value));
  },
  updatePudoCurrentLocation: (latlng, isCollection) => {
    dispatch(updatePudoCurrentLocation(latlng, isCollection));
  },
  showPudoMap: () => {
    dispatch(showPudoMap());
  },
});

Collection.defaultProps = {
  canEdit: true,
};

export default connect(mapStateToProps, mapDispatchToProps)(Collection);
